import React, { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface TierHeader {
  id: string;
  name: string;
  subtitle: string;
  accentColor: string;
  isFilledButton: boolean;
}

interface BenefitRow {
  name: string;
  icon: React.ReactNode;
  tiers: {
    entry: boolean;
    silver: boolean;
    gold: boolean;
    platinum: boolean;
  };
}

const tiersConfig: TierHeader[] = [
  {
    id: 'entry',
    name: 'ENTRY',
    subtitle: 'For startups and small businesses',
    accentColor: '#10B981', // Green
    isFilledButton: false,
  },
  {
    id: 'silver',
    name: 'SILVER',
    subtitle: 'For growing businesses and professionals',
    accentColor: '#6c757d', // Silver/Grey
    isFilledButton: false,
  },
  {
    id: 'gold',
    name: 'GOLD',
    subtitle: 'For established companies and investors',
    accentColor: '#C9952A', // light blue
    isFilledButton: false,
  },
  {
    id: 'platinum',
    name: 'PLATINUM',
    subtitle: 'For leading organizations and strategic partners',
    accentColor: '#0F1C35', // Navy
    isFilledButton: false,
  },
];

const benefitRows: BenefitRow[] = [
  {
    name: 'Access to Networking Events',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    tiers: { entry: true, silver: true, gold: true, platinum: true },
  },
  {
    name: 'Business Matchmaking',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    tiers: { entry: false, silver: true, gold: true, platinum: true },
  },
  {
    name: 'Market Intelligence & Insights',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    tiers: { entry: true, silver: true, gold: true, platinum: true },
  },
  {
    name: 'Training & Capacity Building',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
      </svg>
    ),
    tiers: { entry: true, silver: true, gold: true, platinum: true },
  },
  {
    name: 'Policy Dialogue & Institutional Engagement',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    tiers: { entry: false, silver: false, gold: true, platinum: true },
  },
  {
    name: 'Visibility & Branding Opportunities',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
    tiers: { entry: false, silver: true, gold: true, platinum: true },
  },
  {
    name: 'Priority Access to Delegations & Missions',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2h2a2.5 2.5 0 002.5-2.5V4a9 9 0 01-14.53 6.435z" />
      </svg>
    ),
    tiers: { entry: false, silver: false, gold: true, platinum: true },
  },
  {
    name: 'Dedicated Account Support',
    icon: (
      <svg className="w-4 h-4 text-[#0077b6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    tiers: { entry: false, silver: false, gold: false, platinum: true },
  },
];

const safeHide = (direction: string) => {
  try {
    // @ts-ignore
    if (typeof hide === "function") return hide(direction);
  } catch (e) {}
  return {};
};

export default function MembershipTiersPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleOpenForm = (tierName: string) => {
    setSelectedTier(tierName);
    setModalOpen(true);
    setFormStatus('idle');
    document.body.style.overflow = 'hidden';
  };

  const handleCloseForm = () => {
    setModalOpen(false);
    setFormStatus('idle');
    document.body.style.overflow = '';
  };

   const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const data = new FormData(form);

    const company  = data.get('company')  as string;
    const fullname = data.get('fullname') as string;
    const email    = data.get('email')    as string;
    const phone    = data.get('phone')    as string;
    const industry = data.get('industry') as string;

    const subject = encodeURIComponent(
      `BICCI Membership Application – ${selectedTier} – ${company}`
    );

    const body = encodeURIComponent(
      `Membership Tier: ${selectedTier}\n` +
      `Company Legal Name: ${company}\n` +
      `Representative Name: ${fullname}\n` +
      `Work Email: ${email}\n` +
      `Phone Number: ${phone}\n` +
      `Industry Vertical: ${industry}`
    );

    window.location.href = `mailto:info@bicci-it.com?subject=${subject}&body=${body}`;

    setFormStatus('success');
    form.reset();
    setTimeout(() => handleCloseForm(), 2200);
  };

  return (
    <div className="relative min-h-screen bg-white font-sans">
      <Navbar />
      
      <main className="pt-20">
        {/* Visual Hero Section */}
        <section className="bg-[#0F1C35] pt-32 pb-24 lg:pt-40 lg:pb-32 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[#C9952A]/5 skew-x-12 translate-x-20 pointer-events-none" />
          
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
              <h1
                data-r
                className="text-5xl md:text-5xl lg:text-6xl font-black text-[#0077b6] leading-tight tracking-tight"
                style={safeHide("up")}
              >
                 Membership Categories
              </h1>
              <p
                data-r
                className="mt-6 text-xl text-gray-300 font-light leading-relaxed"
                style={safeHide("up")}
              >
                Become part of a business network connecting Bangladesh and Italy through trade, investment, innovation, and institutional collaboration. 
              </p>
            </div>
          </div>
        </section>

        {/* Matrix Comparison Table Section */}
        <section className="py-20 md:py-24 bg-[#FFF] relative">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24">
            
            {/* Table Header Section */}
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-[#0F1C35] font-extrabold tracking-tight mb-4 text-3xl md:text-4xl lg:text-5xl">
                Choose the Membership <br></br>That Fits Your Goals
              </h2>
              <p className="text-gray-500 font-normal leading-relaxed text-base md:text-lg">
                Join a dynamic network of businesses and institutions working together to strengthen trade and investment between Bangladesh and Italy.
              </p>
            </div>

            {/* Matrix Container */}
            <div className="w-full border border-gray-200/90 rounded-2xl overflow-hidden shadow-xl shadow-black/[0.02] bg-white">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[900px] table-fixed">
                  <thead>
                    <tr className="bg-white border-b border-gray-200">
                      {/* Top Left Title Header Cell */}
                      <th className="p-8 text-left text-xs font-black text-[#0F1C35] uppercase tracking-wider w-[28%] align-bottom">
                        MEMBERSHIP BENEFITS
                      </th>
                      
                      {/* Tier Matrix Column Headers */}
                      {tiersConfig.map((tier) => (
                        <th key={tier.id} className="p-6 text-center w-[18%] relative group align-top border-l border-gray-100">
                          {/* Accent bar strip */}
                          <div 
                            className="absolute top-0 left-0 right-0 h-1 mx-auto w-3/4 rounded-b-md"
                            style={{ backgroundColor: tier.accentColor }}
                          />
                          <div className="pt-4 flex flex-col items-center">
                            <span className="text-lg font-black tracking-wide block" style={{ color: tier.accentColor }}>
                              {tier.name}
                            </span>
                            <span className="text-xs text-gray-400 font-medium leading-tight block mt-2 px-2 max-w-[180px] h-[36px] overflow-hidden">
                              {tier.subtitle}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  
                  <tbody className="divide-y divide-gray-100">
                    {benefitRows.map((row, rowIdx) => (
                      <tr 
                        key={rowIdx} 
                        className="hover:bg-gray-50/40 transition-colors duration-150"
                      >
                        {/* Benefit Row Header Text */}
                        <td className="p-5 md:px-8 flex items-center gap-4 text-sm font-semibold text-[#3D4A62]">
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center">
                            {row.icon}
                          </span>
                          <span className="leading-snug">{row.name}</span>
                        </td>

                        {/* Entry Check/Dash */}
                        <td className="p-5 text-center border-l border-gray-100">
                          {row.tiers.entry ? (
                            <div className="inline-flex items-center justify-center w-5 h-5 text-emerald-600 font-bold">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          ) : (
                            <span className="text-gray-300 font-bold text-lg select-none">—</span>
                          )}
                        </td>

                        {/* Silver Check/Dash */}
                        <td className="p-5 text-center border-l border-gray-100">
                          {row.tiers.silver ? (
                            <div className="inline-flex items-center justify-center w-5 h-5 text-slate-500 font-bold">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          ) : (
                            <span className="text-gray-300 font-bold text-lg select-none">—</span>
                          )}
                        </td>

                        {/* Gold Check/Dash */}
                        <td className="p-5 text-center border-l border-gray-100">
                          {row.tiers.gold ? (
                            <div className="inline-flex items-center justify-center w-5 h-5 text-[#C9952A] font-bold">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          ) : (
                            <span className="text-gray-300 font-bold text-lg select-none">—</span>
                          )}
                        </td>

                        {/* Platinum Check/Dash */}
                        <td className="p-5 text-center border-l border-gray-100">
                          {row.tiers.platinum ? (
                            <div className="inline-flex items-center justify-center w-5 h-5 text-[#0F1C35] font-bold">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          ) : (
                            <span className="text-gray-300 font-bold text-lg select-none">—</span>
                          )}
                        </td>
                      </tr>
                    ))}

                    {/* Bottom Action Buttons Row */}
                    <tr className="bg-white">
                      <td className="p-6 border-t border-gray-200"></td>
                      {tiersConfig.map((tier) => (
                        <td key={tier.id} className="p-6 border-t border-l border-gray-100 text-center">
                          <button
                            onClick={() => handleOpenForm(tier.name)}
                            className={`w-full py-2.5 px-4 rounded-md text-[11px] font-extrabold tracking-wider transition-all cursor-pointer shadow-xs uppercase h-[42px] ${
                              tier.isFilledButton 
                                ? 'text-white' 
                                : 'bg-white hover:bg-gray-50'
                            }`}
                            style={{ 
                              backgroundColor: tier.isFilledButton ? tier.accentColor : 'transparent',
                              border: tier.isFilledButton ? 'none' : `1px solid ${tier.accentColor}`,
                              color: tier.isFilledButton ? '#FFFFFF' : tier.accentColor
                            }}
                          >
                            APPLY NOW
                          </button>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </section>
      </main>

      {/* Dynamic Membership Application Form Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-black text-[#0F1C35]">
                  Apply for {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1).toLowerCase()} Membership
                </h3>
              </div>
              <button onClick={handleCloseForm} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Company Legal Name</label>
                <input name="company" type="text" required className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0077b6] focus:bg-white transition-colors" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Representative Name</label>
                <input name="fullname" type="text" required className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0077b6] focus:bg-white transition-colors" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Work Email</label>
                  <input name="email" type="email" required className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0077b6] focus:bg-white transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Phone Number</label>
                  <input name="phone" type="tel" required className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0077b6] focus:bg-white transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Industry Vertical</label>
                <input name="industry" type="text" placeholder="e.g. Manufacturing, Strategic Trading" required className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0077b6] focus:bg-white transition-colors" />
              </div>

              <button 
                type="submit" 
                disabled={formStatus === 'submitting'} 
                className="w-full py-3.5 mt-4 text-white text-sm font-bold tracking-wider uppercase rounded-lg shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60 cursor-pointer"
                style={{ backgroundColor: '#0077b6' }}
              >
                {formStatus === 'submitting' ? 'Processing...' : 'Confirm Submission'}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 mt-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm font-medium text-emerald-800 flex items-center gap-2">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Form securely dispatched. We will follow up shortly.
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 mt-4 rounded-lg bg-rose-50 border border-rose-200 text-sm font-medium text-rose-800 flex items-center gap-2">
                  <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Submission block occurred. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      
     
    </div>
  );
}